import { ApiHatasi, api } from './api.js';

/**
 * BÜYÜK DOSYA ARŞİVİ — YALNIZCA GOOGLE DRIVE.
 *
 * ─── KURAL ───
 * Post Studio görselleri ve ham log dosyaları HOSTINGER SUNUCU DİSKİNE
 * KALICI OLARAK YAZILMAZ. Tek yol Google Drive'dır.
 *
 * ─── NEDEN SUNUCUYA DÜŞÜLMÜYOR ───
 * Önceki turda Drive bağlı değilken dosya sessizce `/dosyalar` ucuna
 * kaydediliyordu. Kulağa "emniyetli" geliyor ama sonucu şu: kimsenin
 * fark etmediği bir gün Drive kopar, aylarca her görsel ve her ham log
 * sunucuya yazılır, disk dolar ve sistem çöker — üstelik kullanıcı
 * "kaydedildi" gördüğü için hiçbir şey yanlış gitmiş gibi görünmez.
 * Bu yüzden geri düşüş TAMAMEN KALDIRILDI.
 *
 * ─── DRIVE YOKSA NE OLUR ───
 * Dosya arşivlenmez ve kullanıcı bunu AÇIKÇA görür. Emeği kaybolmaz:
 *   · Post Studio'da görsel hazırlanmaya, düzenlenmeye ve CİHAZA
 *     İNDİRİLMEYE devam edilir; yalnızca "Nero Pro arşivine kaydet"
 *     Drive ister.
 *   · Log Analiz'de analiz yapılır ve SONUÇ veritabanına kaydedilir;
 *     yalnızca HAM DOSYA arşivlenemez ve bu ayrıca bildirilir.
 *
 * ─── VERİTABANI YEDEĞİ AYRI KONUDUR ───
 * Günlük iş verisi yedeği Drive olmasa da sunucuda alınır (küçüktür ve
 * acil durum penceresidir). Buradaki kural yalnızca BÜYÜK MEDYA ve HAM
 * LOG içindir. İki şey birbirine karıştırılmamalıdır.
 */

/** Sunucunun "Drive kapalı" dediği kodlar. */
const KAPALI_KODLARI = new Set(['DRIVE_BAGLI_DEGIL', 'DRIVE_YAPILANDIRMASI']);

export const DRIVE_YOK_MESAJI =
  'Google Drive bağlı değil. Bu dosya arşivlenemedi.';

/**
 * Arşivleme başarısız olduğunda fırlatılır.
 *
 * Ayrı bir sınıf olmasının sebebi: çağıran taraf "Drive yok" ile "yükleme
 * patladı"yı ayırt edip doğru cümleyi yazabilsin. İkisinde de sonuç
 * aynıdır — dosya SUNUCUYA YAZILMAZ.
 */
export class ArsivHatasi extends Error {
  constructor(mesaj: string, readonly driveKapali: boolean) {
    super(mesaj);
    this.name = 'ArsivHatasi';
  }
}

export interface ArsivSonucu<T> {
  kayit: T;
  /** Her zaman true olur; alan, çağıranın niyetini okunur kılmak içindir. */
  arsivlendi: true;
}

/**
 * Durum kısa süre önbelleklenir: aynı ekranda arka arkaya yapılan
 * kayıtlarda her seferinde sorulmasın. Yönetici bağlantıyı açıp
 * kapattığında en fazla bir dakika gecikmeyle görülür; sunucu her
 * hâlükârda kendi kapısını ayrıca denetlediği için bu gecikmenin
 * güvenlik bedeli yoktur.
 */
let onbellek: { deger: boolean; son: number } | null = null;
const OMUR_MS = 60_000;

async function arsivAcikMi(): Promise<boolean> {
  if (onbellek && onbellek.son > Date.now()) return onbellek.deger;
  const { acik } = await api.al<{ acik: boolean }>('/dosyalar/arsiv-durumu');
  onbellek = { deger: acik, son: Date.now() + OMUR_MS };
  return acik;
}

/** Bağlantı değişikliğinden sonra: önbelleği unutturur. */
export function arsivDurumunuUnut(): void {
  onbellek = null;
}

/**
 * YİNELEME ANAHTARI — aynı yükleme iki kez oluşmasın.
 *
 * Sunucu bu anahtarı Drive'a damga olarak yazar ve yüklemeden ÖNCE
 * arar. Böylece yükleme tamamlanıp yanıt yolda kaybolsa bile ikinci
 * deneme yeni bir kopya oluşturmaz.
 *
 * Anahtar FORM NESNESİNE yazılır, çağrıya değil: aynı `FormData` ile
 * yapılan her deneme aynı anahtarı taşır (gerçek yeniden deneme), yeni
 * bir kullanıcı eylemi ise yeni bir `FormData` — dolayısıyla yeni bir
 * anahtar — üretir. Bilinçli ikinci yükleme engellenmez.
 */
function yuklemeAnahtariSagla(form: FormData): void {
  if (form.get('upload_id')) return;
  const c = globalThis.crypto as { randomUUID?: () => string } | undefined;
  /* `randomUUID` yalnız güvenli bağlamda vardır; HTTP'li bir yerel
     kurulumda da çalışsın diye yedek üretici bırakıldı. */
  const kimlik = c?.randomUUID?.()
    ?? `y${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  form.append('upload_id', kimlik.replace(/[^A-Za-z0-9_-]/g, ''));
}

/**
 * Dosyayı Google Drive arşivine yükler.
 *
 * Başarısızlıkta HER ZAMAN fırlatır; sunucuya yazmaya ÇALIŞMAZ. Çağıran
 * taraf hatayı kullanıcıya gösterir ve kullanıcı yeniden deneyebilir.
 */
export async function buyukDosyayiArsivle<T>(form: FormData): Promise<ArsivSonucu<T>> {
  yuklemeAnahtariSagla(form);
  let acik: boolean;
  try {
    acik = await arsivAcikMi();
  } catch {
    /* Durum sorulamıyorsa "açık" varsayıp koca dosyayı boşuna yüklemeyiz;
       ama "kapalı" deyip sessizce vazgeçmeyiz de — denenir, sunucu son
       sözü söyler. */
    acik = true;
  }
  if (!acik) throw new ArsivHatasi(DRIVE_YOK_MESAJI, true);

  try {
    return { kayit: await api.dosya<T>('/dosyalar/arsiv', form), arsivlendi: true };
  } catch (h) {
    arsivDurumunuUnut();
    if (h instanceof ApiHatasi && KAPALI_KODLARI.has(h.kod ?? '')) {
      throw new ArsivHatasi(DRIVE_YOK_MESAJI, true);
    }
    const ayrinti = h instanceof Error ? h.message : 'Bilinmeyen hata';
    throw new ArsivHatasi(
      `Google Drive arşivine yüklenemedi: ${ayrinti}. Dosya kaydedilmedi, yeniden deneyebilirsiniz.`,
      false,
    );
  }
}
